import { ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { FormControlEx } from '../../../core/component/forms/form-control';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { LettingAdvertisment } from '../../model/letting-advertisment';
import { DhMarketOptions } from '../../model/platform-options';

@Component({
  selector: 'letting-advertisment-market-area',
  template: `
    <section class="astral-section-surface astral-stack-m">
      <astral-layout-group [columns]="4" [tabletColumns]="2" [mobileColumns]="1">
        <astral-select [control]="form.controls.dhMarketArea" [itemsSource]="marketOptions.areaSelection"></astral-select>
        @if (isNewArea) {
          <astral-input [control]="form.controls.dhMarketAreaName"></astral-input>
        }
      </astral-layout-group>
      <div class="astral-stack-m">
        <letting-translation-control [control]="form.controls.dhMarketAreaDescription" type="area"></letting-translation-control>
        <app-ordered-item-list [control]="form.controls.dhMarketAreaSearchTags"></app-ordered-item-list>
      </div>
    </section>
  `,
  styles: [
    `
      :host {
        display: contents;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LettingAdvertismentMarketAreaComponent implements OnInit, OnChanges, OnDestroy {
  // Widened to AbstractControl so callers can pass form.controls.X (typed AbstractControl)
  // under strictTemplates; internally treated as FormControlEx.
  private _control: FormControlEx;
  @Input({ required: true }) set control(v: AbstractControl) {
    this._control = v as FormControlEx;
  }
  get control(): FormControlEx {
    return this._control;
  }
  @Input({ required: true }) letting: LettingAdvertisment;
  @Input({ required: true }) marketOptions: DhMarketOptions;

  public form: FormGroup;

  protected get isNewArea() {
    return !this.form.controls.dhMarketArea.value;
  }

  private destroy = new Subject<void>();

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly formFactory: FormControlExFactory
  ) {}

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      dhMarketArea: this.formFactory.createWithOptions(
        'LETTING.ADVERTISMENT.DHMARKET.AREA',
        this.letting.dhMarketArea ? this.letting.dhMarketArea : '',
        { isReadonly: this.control.isReadonly }
      ),
      dhMarketAreaName: this.formFactory.createWithOptions('LETTING.ADVERTISMENT.DHMARKET.AREA_NAME', this.letting.dhMarketAreaName, {
        isReadonly: this.control.isReadonly,
      }),
      dhMarketAreaDescription: this.formFactory.createWithOptions(
        'LETTING.ADVERTISMENT.DHMARKET.AREA_DESCRIPTION',
        this.letting.dhMarketAreaDescription,
        {
          isReadonly: this.control.isReadonly || !!this.letting.dhMarketArea,
        }
      ),
      dhMarketAreaSearchTags: this.formFactory.createWithOptions('LETTING.ADVERTISMENT.DHMARKET.AREA_SEARCH_TAGS', [], {
        isReadonly: this.control.isReadonly || !!this.letting.dhMarketArea,
      }),
    });

    this.form.controls.dhMarketArea.valueChanges.pipe(takeUntil(this.destroy)).subscribe({
      next: val => {
        this.updateFormControls();
        this.control.setValue(val);
      },
    });

    this.updateFormControls();
  }

  ngOnDestroy(): void {
    this.destroy.next();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.control || changes.letting) && this.form) {
      this.updateFormControls();
      this.applyReadonly(this.form);
    }
  }

  private updateFormControls() {
    const selectedArea = this.marketOptions.areas?.find(a => a.id === this.form.controls.dhMarketArea.value);
    if (!selectedArea && this.form.controls.dhMarketArea.value !== '') {
      this.form.controls.dhMarketArea.setValue('');
      return;
    }

    const dhMarketAreaName = this.form.controls.dhMarketAreaName as FormControlEx;
    dhMarketAreaName.required = !selectedArea;
    dhMarketAreaName.markAsUntouched();
    dhMarketAreaName.updateValueAndValidity();

    const dhMarketAreaDescription = this.form.controls.dhMarketAreaDescription as FormControlEx;
    dhMarketAreaDescription.setValue(selectedArea ? selectedArea.description : []);
    dhMarketAreaDescription.required = !selectedArea;
    dhMarketAreaDescription.isReadonly = !!selectedArea;
    dhMarketAreaDescription.markAsUntouched();
    dhMarketAreaDescription.updateValueAndValidity();

    const dhMarketAreaSearchTags = this.form.controls.dhMarketAreaSearchTags as FormControlEx;
    dhMarketAreaSearchTags.setValue(selectedArea ? selectedArea.searchTags : []);
    dhMarketAreaSearchTags.isReadonly = !!selectedArea;
    dhMarketAreaSearchTags.markAsUntouched();
    dhMarketAreaSearchTags.updateValueAndValidity();
  }

  private applyReadonly(control: AbstractControl): void {
    if (control instanceof FormGroup || control instanceof FormArray) {
      Object.keys(control.controls).forEach(key => this.applyReadonly(control.controls[key]));
      return;
    }

    if (control instanceof FormControlEx) {
      control.isReadonly = this.control.isReadonly;
    }

    control.disable();
    control.updateValueAndValidity({ emitEvent: false });
  }
}
